import { createOrder } from "@/apis/tms/order";
import Indicator from "@/components/shared/Indicator";
import { useGlobalContext } from "@/hooks/context";
import {
  DialogRef,
  IntegrationOrderDetailVo,
  TMSScanOrderCreateDo,
} from "@/types";
import { Button, Modal } from "antd";
import classNames from "classnames";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";

interface OrderConfirmProps {
  order: IntegrationOrderDetailVo;
  productCartons: Record<string, number>;
  orderDo: Omit<TMSScanOrderCreateDo, "productCartons">;
}

interface OrderConfirmDialogProps {
  className?: string;
}

const OrderConfirmDialog = forwardRef<DialogRef, OrderConfirmDialogProps>(
  (props, ref) => {
    const { className } = props;
    const { hintPrompt } = useGlobalContext();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [order, setOrder] = useState<IntegrationOrderDetailVo | null>(null);
    const [orderDo, setOrderDo] = useState<Omit<
      TMSScanOrderCreateDo,
      "productCartons"
    > | null>(null);
    const [productCartons, setProductCartons] = useState<
      Record<string, number>
    >({});
    const [loading, setLoading] = useState(false);

    const closeDialog = useCallback(() => {
      setIsModalOpen(false);
      setOrder(null);
      setOrderDo(null);
      setProductCartons({});
    }, []);

    const confirmCreate = useCallback(() => {
      if (order == null || orderDo == null) {
        hintPrompt("error", "Invalid data", "Something happened");
        return;
      }
      setLoading(true);
      const products = Object.entries(productCartons).map(
        ([productNo, cartons]) => {
          const product = order?.products.find(
            (product) => product.productNo === productNo
          );
          if (product == null)
            throw new Error(`Product ${productNo} not found`);
          return {
            productNo: productNo,
            quantity: cartons,
            qtyPerCarton: product.quantity / cartons,
          };
        }
      );
      const body: TMSScanOrderCreateDo = {
        ...orderDo,
        productCartons: products,
      };
      createOrder(body)
        .then(() => {
          hintPrompt("success", "Order created successfully");
          closeDialog();
        })
        .catch((error) => {
          hintPrompt(
            "error",
            "Failed to create order",
            error?.msg ?? "Unknown error"
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }, [productCartons, order, hintPrompt, closeDialog, orderDo]);

    useImperativeHandle(ref, () => ({
      showModal: (props) => {
        const { order, orderDo, productCartons } = props as OrderConfirmProps;
        setOrder(order);
        setProductCartons(productCartons);
        setOrderDo(orderDo);
        setIsModalOpen(true);
      },
      hideModal: () => {
        setIsModalOpen(false);
        setOrder(null);
        setOrderDo(null);
        setProductCartons({});
      },
    }));

    return (
      <Modal
        title={`Confirm for Order ${order?.customerNumber}`}
        open={isModalOpen}
        onCancel={closeDialog}
        footer={null}
        maskClosable={false}
        loading={loading}
      >
        <div className={className}>
          <Indicator label="Products List" />
          {/* {orderDo?.cubeOrderNo} */}
          <div className="border-b border-gray-200 border-[1px] rounded-md">
            {Object.entries(productCartons).map(([productNo, cartons]) => (
              <div
                key={productNo}
                className={classNames(
                  "flex flex-col",
                  "border-b border-gray-200 border-b-[1px]",
                  "last:border-b-0",
                  "px-4 py-2 w-full"
                )}
              >
                <div className="flex flex-row items-center justify-between gap-2">
                  <span className="text-sm font-medium">{productNo}</span>
                  <div>
                    <span className="text-sm text-gray-500 w-[100px] text-right">
                      {cartons} cartons
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-row gap-2 justify-end pt-4">
            <Button onClick={closeDialog}>Cancel</Button>
            <Button type="primary" onClick={confirmCreate}>
              Create
            </Button>
          </div>
        </div>
      </Modal>
    );
  }
);

OrderConfirmDialog.displayName = "OrderConfirmDialog";

export default OrderConfirmDialog;
